import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { ToastContext, setToastSink, useToast, type Toast } from "./toast-context";

/**
 * A place for failures to appear.
 *
 * 20 of the portal's 43 mutations had no `onError`, so pausing an outbox
 * consumer, replaying a DLQ, cancelling a reindex or toggling a flag could
 * fail and show nothing at all. On an incident console that is worse than an
 * error: you believe the thing you clicked happened.
 *
 * Deliberately a global surface rather than 20 hand-written handlers — the
 * 21st mutation would have been silent too. It is wired to react-query's
 * MutationCache in main.tsx, so a mutation has to opt *out* of being seen.
 */

let nextId = 1;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const push = useCallback(
    (text: string, tone: Toast["tone"] = "error") => {
      const id = nextId++;
      // Cap the stack: a failing poll can fire repeatedly, and forty stacked
      // copies of the same message hide the page behind them.
      setToasts((t) => [...t.slice(-3), { id, text, tone }]);
      window.setTimeout(() => dismiss(id), 8000);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toasts, push, dismiss }), [toasts, push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      <ToastBridge />
      {children}
      <div
        // Above the mobile header, clear of the iOS home indicator, and never
        // wider than the viewport.
        className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]"
        role="status"
        aria-live="polite"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cn(
              "pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-lg border p-3 text-sm shadow-lg",
              t.tone === "error"
                ? "border-destructive/40 bg-destructive/15 text-red-200 backdrop-blur"
                : "border-border bg-card text-foreground",
            )}
          >
            {t.tone === "error" && (
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
            )}
            <span className="min-w-0 flex-1 break-words">{t.text}</span>
            <button
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss"
              className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * Connects the context to `emitToast` so the react-query caches in main.tsx —
 * which run outside the React tree — can raise one.
 */
export function ToastBridge() {
  const { push } = useToast();
  useEffect(() => {
    setToastSink(push);
    return () => setToastSink(null);
  }, [push]);
  return null;
}
